import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  DEFAULT_WINDOW_SIZE,
  WINDOW_PRESETS,
  type CreateBrowserProfileBody,
  type NodeDto,
} from '@br/protocol';
import { errorMessage } from '../api/client';
import { useCreateProfile, useNodes, type CreateProfileResponse } from '../api/queries';
import { Alert, Button, Checkbox, CopyButton, Field, Input, Modal, Select } from '../components/ui';
import { MODE_LABEL } from '../lib/labels';

export const PROXY_RE = /^(https?|socks5?):\/\/[^\s/]+$/i;

function presetLabel(label: string): string {
  return label
    .replace('Desktop', '데스크톱')
    .replace('Narrow', '좁은 화면')
    .replace('Mobile-ish', '모바일형');
}

type HeadlessChoice = 'inherit' | 'on' | 'off';
type IdleChoice = 'inherit' | 'never' | 'custom';

interface FormState {
  nodeId: string;
  name: string;
  customerLabel: string;
  initialUrl: string;
  size: string; // "WxH" or "custom"
  customWidth: string;
  customHeight: string;
  proxyEnabled: boolean;
  proxyServer: string;
  proxyUsername: string;
  proxyPassword: string;
  proxyBypass: string;
  headless: HeadlessChoice;
  locale: string;
  timezoneId: string;
  idle: IdleChoice;
  idleMinutes: string;
  openNow: boolean;
  createSupportSession: boolean;
}

export const LOCALE_RE = /^[a-zA-Z]{2,3}(-[a-zA-Z0-9]{2,8})*$/;
export function isKnownTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function initialForm(nodes: NodeDto[]): FormState {
  const online = nodes.find((n) => n.online) ?? nodes[0];
  return {
    nodeId: online?.id ?? '',
    name: '',
    customerLabel: '',
    initialUrl: '',
    size: `${DEFAULT_WINDOW_SIZE.width}x${DEFAULT_WINDOW_SIZE.height}`,
    customWidth: String(DEFAULT_WINDOW_SIZE.width),
    customHeight: String(DEFAULT_WINDOW_SIZE.height),
    proxyEnabled: false,
    proxyServer: '',
    proxyUsername: '',
    proxyPassword: '',
    proxyBypass: '',
    headless: 'inherit',
    locale: '',
    timezoneId: '',
    idle: 'inherit',
    idleMinutes: '30',
    openNow: true,
    createSupportSession: true,
  };
}

export function ProfileCreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nodes = useNodes();
  const create = useCreateProfile();
  const navigate = useNavigate();
  const sortedNodes = useMemo(
    () => [...(nodes.data ?? [])].sort((a, b) => Number(b.online) - Number(a.online)),
    [nodes.data],
  );
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateProfileResponse | null>(null);
  const f = form ?? initialForm(sortedNodes);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm({ ...f, [k]: v });

  const close = () => {
    setForm(null);
    setError(null);
    setResult(null);
    onClose();
  };

  const submit = () => {
    setError(null);
    if (!f.nodeId) return setError('노드를 선택하세요');
    if (!f.name.trim()) return setError('프로필 이름을 입력하세요');
    let size = DEFAULT_WINDOW_SIZE;
    if (f.size === 'custom') {
      const w = Number(f.customWidth);
      const h = Number(f.customHeight);
      if (!(w >= 320 && w <= 3840 && h >= 240 && h <= 2160))
        return setError('창 크기는 가로 320~3840, 세로 240~2160 사이여야 합니다');
      size = { width: w, height: h };
    } else {
      const [w, h] = f.size.split('x').map(Number);
      if (w && h) size = { width: w, height: h };
    }
    if (f.proxyEnabled && !PROXY_RE.test(f.proxyServer.trim()))
      return setError('프록시 주소 형식이 올바르지 않습니다 (예: http://host:port)');
    const locale = f.locale.trim();
    const timezoneId = f.timezoneId.trim();
    if (locale && !LOCALE_RE.test(locale))
      return setError('언어 형식이 올바르지 않습니다 (예: en-US)');
    if (timezoneId && !isKnownTimezone(timezoneId))
      return setError('시간대 형식이 올바르지 않습니다 (예: America/New_York)');
    const idleMinutes = Number(f.idleMinutes);
    if (
      f.idle === 'custom' &&
      (!Number.isInteger(idleMinutes) || idleMinutes < 1 || idleMinutes > 1440)
    )
      return setError('자동 종료 시간은 1~1440분 사이의 정수여야 합니다');
    const body: CreateBrowserProfileBody = {
      nodeId: f.nodeId,
      name: f.name.trim(),
      customerLabel: f.customerLabel.trim(),
      initialUrl: f.initialUrl.trim() || undefined,
      defaultMode: 'screencast',
      size,
      proxy: f.proxyEnabled
        ? {
            server: f.proxyServer.trim(),
            username: f.proxyUsername.trim() || undefined,
            password: f.proxyPassword || undefined,
            bypass: f.proxyBypass.trim() || undefined,
          }
        : null,
      headless: f.headless === 'inherit' ? null : f.headless === 'on',
      locale: locale || null,
      timezoneId: timezoneId || null,
      idleMinutes: f.idle === 'inherit' ? null : f.idle === 'never' ? 0 : idleMinutes,
      openNow: f.openNow,
      createSupportSession: f.createSupportSession,
    };
    create.mutate(body, {
      onSuccess: (r) => setResult(r),
      onError: (err) => setError(errorMessage(err)),
    });
  };

  if (result) {
    return (
      <Modal
        open={open}
        title="브라우저 프로필이 생성되었습니다"
        onClose={close}
        footer={
          <>
            <Button onClick={close}>닫기</Button>
            <Button onClick={() => navigate(`/profiles/${result.profile.id}`)}>프로필 상세</Button>
            <Button
              variant="primary"
              onClick={() => navigate(`/profiles/${result.profile.id}/view`)}
            >
              뷰어 열기
            </Button>
          </>
        }
      >
        <div className="space-y-4 text-sm">
          <p className="font-medium text-ink">{result.profile.name}</p>
          {result.openError && (
            <Alert level="warning">브라우저를 바로 열지 못했습니다: {result.openError}</Alert>
          )}
          {result.supportSession ? (
            <div className="space-y-3 rounded-lg border border-line bg-canvas p-3">
              <p className="text-xs font-medium text-ink-muted">
                고객에게 아래 지원 세션 링크 또는 접속 코드를 전달하세요
              </p>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded bg-surface px-2 py-1 text-xs">
                  {result.supportSession.url}
                </code>
                <CopyButton text={result.supportSession.url} label="링크 복사" />
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-surface px-2 py-1 font-mono text-lg tracking-widest">
                  {result.supportSession.code}
                </code>
                <CopyButton text={result.supportSession.code} label="코드 복사" />
              </div>
              <p className="text-xs text-ink-muted">
                코드 입력 주소: <code>{window.location.origin}/join</code>
              </p>
            </div>
          ) : (
            <p className="text-ink-muted">지원 세션은 프로필 상세에서 나중에 만들 수 있습니다.</p>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title="새 프로필"
      onClose={close}
      size="lg"
      footer={
        <>
          <Button onClick={close}>취소</Button>
          <Button variant="primary" onClick={submit} loading={create.isPending}>
            만들기
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="노드" required className="sm:col-span-2">
          <Select value={f.nodeId} onChange={(e) => set('nodeId', e.target.value)}>
            {sortedNodes.length === 0 && <option value="">등록된 노드가 없습니다</option>}
            {sortedNodes.map((n) => (
              <option key={n.id} value={n.id}>
                {n.online ? '● ' : '○ '}
                {n.name}
                {n.online ? '' : ' (오프라인)'}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="프로필 이름" required>
          <Input
            value={f.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="예: 홍길동 · 광고 계정"
            autoFocus
          />
        </Field>
        <Field label="고객 라벨">
          <Input
            value={f.customerLabel}
            onChange={(e) => set('customerLabel', e.target.value)}
            placeholder="고객 이름/회사"
          />
        </Field>
        <Field label="초기 URL" hint="첫 탭에서 열 주소. 비우면 빈 탭" className="sm:col-span-2">
          <Input
            inputMode="url"
            value={f.initialUrl}
            onChange={(e) => set('initialUrl', e.target.value)}
            placeholder="https://"
          />
        </Field>
        <Field label="기본 전송 모드">
          <Select value="screencast" disabled>
            <option value="screencast">{MODE_LABEL.screencast}</option>
          </Select>
        </Field>
        <Field label="창 크기">
          <Select value={f.size} onChange={(e) => set('size', e.target.value)}>
            {WINDOW_PRESETS.map((p) => (
              <option key={p.label} value={`${p.width}x${p.height}`}>
                {presetLabel(p.label)}
              </option>
            ))}
            <option value="custom">직접 입력</option>
          </Select>
        </Field>
        {f.size === 'custom' && (
          <div className="grid grid-cols-2 gap-3 sm:col-span-2">
            <Field label="가로 (px)">
              <Input
                type="number"
                min={320}
                max={3840}
                value={f.customWidth}
                onChange={(e) => set('customWidth', e.target.value)}
              />
            </Field>
            <Field label="세로 (px)">
              <Input
                type="number"
                min={240}
                max={2160}
                value={f.customHeight}
                onChange={(e) => set('customHeight', e.target.value)}
              />
            </Field>
          </div>
        )}
        <Field label="headless" hint="노드 GUI 설정을 덮어씁니다. headless는 스텔스가 약해집니다">
          <Select
            value={f.headless}
            onChange={(e) => set('headless', e.target.value as HeadlessChoice)}
          >
            <option value="inherit">노드 설정 따름</option>
            <option value="off">끔 (창 표시)</option>
            <option value="on">켬</option>
          </Select>
        </Field>
        <div className="flex flex-col justify-end gap-2">
          <Checkbox
            label="지금 브라우저 열기"
            checked={f.openNow}
            onChange={(e) => set('openNow', e.target.checked)}
          />
          <Checkbox
            label="고객 지원 세션 함께 생성"
            checked={f.createSupportSession}
            onChange={(e) => set('createSupportSession', e.target.checked)}
          />
        </div>
        <Field
          label="접속자가 없을 때 자동 종료"
          hint="Chrome 프로세스만 닫고 브라우저 프로필 데이터는 삭제하지 않습니다"
          className="sm:col-span-2"
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <Select value={f.idle} onChange={(e) => set('idle', e.target.value as IdleChoice)}>
              <option value="inherit">서버 기본값 사용</option>
              <option value="never">자동 종료 안 함</option>
              <option value="custom">직접 지정</option>
            </Select>
            {f.idle === 'custom' && (
              <Input
                type="number"
                min={1}
                max={1440}
                value={f.idleMinutes}
                onChange={(e) => set('idleMinutes', e.target.value)}
                aria-label="자동 종료 시간(분)"
                placeholder="분"
              />
            )}
          </div>
        </Field>
        <div className="sm:col-span-2 rounded-lg border border-line p-3">
          <Checkbox
            label="HTTP 프록시 사용"
            checked={f.proxyEnabled}
            onChange={(e) => set('proxyEnabled', e.target.checked)}
          />
          {f.proxyEnabled && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field
                label="서버"
                required
                hint="http://host:port (SOCKS5 인증은 지원하지 않음)"
                className="sm:col-span-2"
              >
                <Input
                  value={f.proxyServer}
                  onChange={(e) => set('proxyServer', e.target.value)}
                  placeholder="http://proxy.example.com:8080"
                />
              </Field>
              <Field label="사용자 이름">
                <Input
                  value={f.proxyUsername}
                  onChange={(e) => set('proxyUsername', e.target.value)}
                  autoComplete="off"
                />
              </Field>
              <Field label="비밀번호">
                <Input
                  type="password"
                  value={f.proxyPassword}
                  onChange={(e) => set('proxyPassword', e.target.value)}
                  autoComplete="new-password"
                />
              </Field>
              <Field
                label="우회 목록"
                hint="쉼표로 구분, 예: localhost,*.internal"
                className="sm:col-span-2"
              >
                <Input value={f.proxyBypass} onChange={(e) => set('proxyBypass', e.target.value)} />
              </Field>
            </div>
          )}
        </div>
        <RegionFields
          locale={f.locale}
          timezoneId={f.timezoneId}
          onLocale={(v) => set('locale', v)}
          onTimezone={(v) => set('timezoneId', v)}
        />
        {error && (
          <div className="sm:col-span-2">
            <Alert level="error">{error}</Alert>
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * Region the browser presents. Left empty the node's system settings are used; when a profile goes
 * through a proxy these should match the proxy's exit region, otherwise the mismatch itself reads
 * as suspicious to the site the customer logs into.
 */
export function RegionFields({
  locale,
  timezoneId,
  onLocale,
  onTimezone,
}: {
  locale: string;
  timezoneId: string;
  onLocale: (v: string) => void;
  onTimezone: (v: string) => void;
}) {
  return (
    <div className="sm:col-span-2 rounded-lg border border-line p-3">
      <p className="text-sm font-medium text-ink">브라우저 지역</p>
      <p className="mt-0.5 text-xs text-ink-muted">
        비우면 노드 PC 설정을 따릅니다. 프록시를 쓰면 프록시 출구 지역에 맞추세요. 언어·시간대가
        IP와 어긋나면 그 자체가 의심 신호가 됩니다.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="언어 (locale)" hint="예: ko-KR, en-US, ja-JP">
          <Input
            value={locale}
            onChange={(e) => onLocale(e.target.value)}
            placeholder="노드 설정 따름"
            autoComplete="off"
          />
        </Field>
        <Field label="시간대" hint="예: Asia/Seoul, America/New_York">
          <Input
            value={timezoneId}
            onChange={(e) => onTimezone(e.target.value)}
            placeholder="노드 설정 따름"
            autoComplete="off"
          />
        </Field>
      </div>
    </div>
  );
}
